<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { WALLET_MODULE } from "@/state/modules/wallet";
import { walletMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { walletService } from "@/helpers/services/wallet.service";
import { sharedHelper } from "@/utils";

/**
 * One agent's wallet: the balance, the log, and the two ways it can be changed
 * from the back office.
 *
 * The log is the audit trail — every row keeps the balance before and after, so
 * a disputed withdrawal can be answered from the row itself.
 */
export default {
  page() {
    return { title: i18n.t("module.wallet") };
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      wallet: null,
      loading: true,
      currentPage: 1,
      perPage: 20,
      // Withdrawal dialog. No amount: it always takes the whole balance.
      showWithdraw: false,
      withdrawRemarks: "",
      // Adjustment dialog
      showAdjust: false,
      adjustAmount: null,
      adjustType: "credit",
      adjustRemarks: "",
      selectedAdjustType: null,
      submitting: false,
      fields: [
        { key: "createdAt", label: i18n.t("label.date") },
        { key: "source", label: i18n.t("label.type") },
        { key: "amount", label: i18n.t("label.amount") },
        { key: "balanceAfter", label: i18n.t("wallet.balanceAfter") },
        { key: "refDocNo", label: i18n.t("label.docNo") },
        { key: "actorName", label: i18n.t("wallet.actor") },
        { key: "remarks", label: i18n.t("label.remarks") },
      ],
    };
  },
  computed: {
    ...mapState(WALLET_MODULE, ["walletTransactions"]),
    userUuid() {
      return this.$route.params.userUuid;
    },
    tableRows() {
      return this.walletTransactions.data;
    },
    totalRows() {
      return this.walletTransactions.meta?.total ?? this.walletTransactions.data.length;
    },
    agentName() {
      return this.wallet?.userName || "—";
    },
    balance() {
      return Number(this.wallet?.balance ?? 0);
    },
    adjustTypeOptions() {
      return [
        { value: "credit", text: i18n.t("wallet.typeCredit") },
        { value: "debit", text: i18n.t("wallet.typeDebit") },
      ];
    },
    title() {
      return `${i18n.t("module.wallet")} — ${this.agentName}`;
    },
    breadcrumbItems() {
      return [
        { text: i18n.t("module.admin"), href: "/" },
        { text: i18n.t("module.wallet"), href: "/wallets" },
        { text: this.agentName, active: true },
      ];
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    ...walletMethods,
    async load() {
      this.loading = true;
      try {
        // The header comes straight off the service: it is one row and does not
        // need to sit in the store, where a second wallet page would clobber it.
        const r = await walletService.get(this.userUuid);
        const d = r?.data || {};
        this.wallet = {
          userName: d.user_name,
          agentCode: d.agent_code,
          balance: Number(d.balance ?? 0),
          currencyCode: d.currency_code || "MYR",
          collectedToday: Number(d.collected_today ?? 0),
        };
        await this.loadTransactions();
      } catch (e) {
        sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code);
      } finally {
        this.loading = false;
      }
    },
    loadTransactions() {
      return this.getWalletTransactions({
        userUuid: this.userUuid,
        params: { page: this.currentPage, size: this.perPage },
      });
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadTransactions();
    },
    money(v) {
      return Number(v ?? 0).toFixed(2);
    },
    sourceLabel(source) {
      const map = {
        cash_sale: "sourceCashSale",
        withdrawal: "sourceWithdrawal",
        adjustment: "sourceAdjustment",
        reversal: "sourceReversal",
      };
      return i18n.t(`wallet.${map[source] || "sourceAdjustment"}`);
    },
    sourceVariant(source) {
      return {
        cash_sale: "success",
        withdrawal: "info",
        adjustment: "warning",
        reversal: "danger",
      }[source] || "secondary";
    },
    openWithdraw() {
      this.withdrawRemarks = "";
      this.showWithdraw = true;
    },
    async confirmWithdraw() {
      if (this.balance <= 0) return;

      this.submitting = true;
      try {
        // No amount is sent. The API takes whatever the wallet holds when it
        // runs, so a sale landing while this dialog was open is taken in with
        // the rest instead of being left behind as an unexplainable balance.
        const result = await this.withdrawFromWallet({
          userUuid: this.userUuid,
          data: { remarks: this.withdrawRemarks || undefined },
        });
        // The dialog stays open on failure so the amount can be corrected.
        if (result) {
          this.showWithdraw = false;
          await this.load();
        }
      } finally {
        this.submitting = false;
      }
    },
    openAdjust() {
      this.adjustAmount = null;
      this.adjustType = "credit";
      this.selectedAdjustType = this.adjustTypeOptions[0];
      this.adjustRemarks = "";
      this.showAdjust = true;
    },
    onAdjustTypeChange(t) {
      this.selectedAdjustType = t;
      this.adjustType = t ? t.value : "credit";
    },
    async confirmAdjust() {
      if (!this.adjustAmount || Number(this.adjustAmount) <= 0) return;
      if (!String(this.adjustRemarks || "").trim()) return;

      this.submitting = true;
      try {
        const result = await this.adjustWallet({
          userUuid: this.userUuid,
          data: {
            amount: Number(this.adjustAmount),
            type: this.adjustType,
            remarks: this.adjustRemarks,
          },
        });
        if (result) {
          this.showAdjust = false;
          await this.load();
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-row class="align-items-center">
              <b-col cols="12" md="8">
                <h5 class="mb-1">
                  {{ agentName }}
                  <small v-if="wallet && wallet.agentCode" class="text-muted font-monospace ml-1">
                    {{ wallet.agentCode }}
                  </small>
                </h5>
                <div class="text-muted small">
                  {{ $t('wallet.collectedToday') }}:
                  <strong class="text-success">{{ money(wallet && wallet.collectedToday) }}</strong>
                </div>
              </b-col>
              <b-col cols="12" md="4" class="text-md-right mt-3 mt-md-0">
                <div class="text-muted small">{{ $t('wallet.balance') }}</div>
                <h3 :class="balance < 0 ? 'text-danger mb-2' : 'text-primary mb-2'">
                  {{ money(balance) }}
                </h3>
                <b-button
                  v-if="$can('Update Wallet')"
                  variant="primary"
                  size="sm"
                  :disabled="balance <= 0"
                  @click="openWithdraw"
                >
                  <i class="mdi mdi-cash-check mr-1"></i>{{ $t('wallet.withdraw') }}
                </b-button>
                <b-button
                  v-if="$can('Update Wallet')"
                  variant="outline-secondary"
                  size="sm"
                  class="ml-1"
                  @click="openAdjust"
                >
                  <i class="mdi mdi-tune mr-1"></i>{{ $t('wallet.adjust') }}
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="text-primary mb-3">{{ $t('wallet.transactions') }}</h6>

            <b-table :items="tableRows" :fields="fields" responsive="sm" small show-empty>
              <template #empty>
                <div class="text-center text-muted py-3">{{ $t('wallet.noTransactions') }}</div>
              </template>

              <template #cell(source)="data">
                <b-badge :variant="sourceVariant(tableRows[data.index].source)">
                  {{ sourceLabel(tableRows[data.index].source) }}
                </b-badge>
              </template>

              <template #cell(amount)="data">
                <strong :class="tableRows[data.index].type === 'debit' ? 'text-danger' : 'text-success'">
                  {{ tableRows[data.index].type === 'debit' ? '−' : '+' }}{{ money(tableRows[data.index].amount) }}
                </strong>
              </template>

              <template #cell(balanceAfter)="data">
                {{ money(tableRows[data.index].balanceAfter) }}
              </template>

              <template #cell(refDocNo)="data">
                <span v-if="tableRows[data.index].refDocNo" class="font-monospace">
                  {{ tableRows[data.index].refDocNo }}
                </span>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(actorName)="data">
                {{ tableRows[data.index].actorName || '—' }}
              </template>

              <template #cell(remarks)="data">
                <span class="small">{{ tableRows[data.index].remarks || '—' }}</span>
              </template>
            </b-table>

            <b-pagination
              v-if="totalRows > perPage"
              :value="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              class="mt-3 mb-0"
              @change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Take cash in -->
    <b-modal v-model="showWithdraw" :title="$t('wallet.withdrawTitle', [agentName])" centered hide-footer>
      <p class="text-muted small">{{ $t('wallet.withdrawHint') }}</p>

      <!-- Nothing to type. Handing in cash is not a negotiation: the agent
           empties their bag and the back office counts it. -->
      <div class="card bg-light mb-3">
        <div class="card-body py-3 text-center">
          <div class="text-muted small">{{ $t('wallet.willTakeIn') }}</div>
          <h3 class="text-primary mb-0">{{ money(balance) }}</h3>
        </div>
      </div>

      <b-row>
        <b-col cols="12">
          <b-form-group :label="$t('label.remarks')">
            <b-form-textarea v-model="withdrawRemarks" rows="2" />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="text-right mt-2">
        <b-button variant="light" class="mr-2" @click="showWithdraw = false">{{ $t('label.cancel') }}</b-button>
        <b-button variant="primary" :disabled="submitting || balance <= 0" @click="confirmWithdraw">
          <b-spinner v-if="submitting" small class="mr-1" />
          {{ $t('wallet.withdraw') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Manual correction -->
    <b-modal v-model="showAdjust" :title="$t('wallet.adjustTitle', [agentName])" centered hide-footer>
      <p class="text-muted small">{{ $t('wallet.adjustHint') }}</p>
      <b-row>
        <b-col cols="12">
          <b-form-group :label="$t('wallet.direction')">
            <multiselect
              :value="selectedAdjustType"
              :options="adjustTypeOptions"
              label="text"
              track-by="value"
              :allow-empty="false"
              :show-labels="false"
              open-direction="below"
              @input="onAdjustTypeChange"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group :label="$t('label.amount')">
            <b-form-input
              v-model.number="adjustAmount"
              type="number"
              min="0"
              step="0.01"
              :state="adjustAmount != null && Number(adjustAmount) <= 0 ? false : null"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group :label="$t('wallet.reason')">
            <b-form-textarea
              v-model="adjustRemarks"
              rows="2"
              :state="adjustRemarks !== '' || !showAdjust ? null : false"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="text-right mt-2">
        <b-button variant="light" class="mr-2" @click="showAdjust = false">{{ $t('label.cancel') }}</b-button>
        <b-button
          variant="primary"
          :disabled="submitting || !adjustAmount || Number(adjustAmount) <= 0 || !String(adjustRemarks).trim()"
          @click="confirmAdjust"
        >
          <b-spinner v-if="submitting" small class="mr-1" />
          {{ $t('label.submit') }}
        </b-button>
      </div>
    </b-modal>
  </Layout>
</template>
